import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import logger from 'utils/logger';
import { useLocation } from 'react-router-dom';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, setDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import './AdminPopup.css';

/** Coerce Firestore Timestamp-like values to epoch ms (handles plain { seconds } objects). */
const popupDismissalExpiresAtMillis = expiresAt => {
  if (expiresAt === null || expiresAt === undefined) {
    return null;
  }
  if (typeof expiresAt === 'number' && Number.isFinite(expiresAt)) {
    return expiresAt > 1e12 ? expiresAt : expiresAt * 1000;
  }
  if (typeof expiresAt.toMillis === 'function') {
    try {
      const ms = expiresAt.toMillis();
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
    } catch {
      return null;
    }
  }
  if (typeof expiresAt.toDate === 'function') {
    try {
      const d = expiresAt.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : null;
    } catch {
      return null;
    }
  }
  if (typeof expiresAt === 'object') {
    const sec =
      typeof expiresAt.seconds === 'number'
        ? expiresAt.seconds
        : typeof expiresAt._seconds === 'number'
          ? expiresAt._seconds
          : null;
    const nano =
      typeof expiresAt.nanoseconds === 'number'
        ? expiresAt.nanoseconds
        : typeof expiresAt._nanoseconds === 'number'
          ? expiresAt._nanoseconds
          : 0;
    if (typeof sec === 'number') {
      return sec * 1000 + nano / 1e6;
    }
  }
  if (expiresAt instanceof Date && !Number.isNaN(expiresAt.getTime())) {
    return expiresAt.getTime();
  }
  return null;
};

const isPopupDismissalStillActive = expiresAt => {
  const ms = popupDismissalExpiresAtMillis(expiresAt);
  return typeof ms === 'number' && Number.isFinite(ms) && ms > Date.now();
};

// Debounce helper
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const AdminPopup = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const isHomeOnlyPath = location.pathname === '/';

  const [popupData, setPopupData] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const checkPopup = useCallback(async () => {
    if (!currentUser) {
      return;
    }

    try {
      const token = await currentUser.getIdTokenResult(false);
      const claims = token.claims || {};
      const isAuthorized =
        claims.admin === true ||
        claims.manager === true ||
        claims.staff === true ||
        claims.executive === true;
      if (!isAuthorized) {
        return;
      }

      // Check if popup is enabled
      const popupRef = doc(db, 'adminSettings', 'sitePopup');
      const popupSnap = await getDoc(popupRef);

      if (!popupSnap.exists() || !popupSnap.data().enabled) {
        return;
      }

      // Firestore dismissal is source of truth; localStorage can be stale after admin "reset dismissals"
      const dismissalRef = doc(db, 'userDismissals', currentUser.uid);
      const dismissalSnap = await getDoc(dismissalRef);

      const firestoreStillActive =
        dismissalSnap.exists() && isPopupDismissalStillActive(dismissalSnap.data().expiresAt);

      if (firestoreStillActive) {
        setIsDismissed(true);
        return;
      }

      // No active server dismissal — drop stale popup flag so reset-dismissals works
      localStorage.removeItem(`popupDismissed_${currentUser.uid}`);

      setPopupData(popupSnap.data());
      setShowPopup(true);
    } catch (error) {
      logger.error('Popup check error:', error);
    }
  }, [currentUser]);

  const checkPopupRef = useRef(checkPopup);
  checkPopupRef.current = checkPopup;

  const debouncedCheckPopup = useMemo(
    () =>
      debounce(() => {
        void checkPopupRef.current();
      }, 1000),
    []
  );

  // When admin clears userDismissals (or expiry passes), allow popup again without full page reload
  useEffect(() => {
    if (!currentUser?.uid) {
      return;
    }
    const dismissalRef = doc(db, 'userDismissals', currentUser.uid);
    const unsub = onSnapshot(
      dismissalRef,
      snap => {
        const active = snap.exists() && isPopupDismissalStillActive(snap.data().expiresAt);
        if (!active) {
          localStorage.removeItem(`popupDismissed_${currentUser.uid}`);
          setIsDismissed(false);
          void checkPopup();
        }
      },
      err => logger.error('Popup dismissal listener error:', err)
    );
    return () => unsub();
  }, [currentUser?.uid, checkPopup]);

  useEffect(() => {
    if (!currentUser || isClosing || isDismissed || !isHomeOnlyPath) {
      return;
    }
    debouncedCheckPopup();
  }, [currentUser, isClosing, isDismissed, debouncedCheckPopup, isHomeOnlyPath]);

  const handleClose = async () => {
    setIsClosing(true);
    try {
      if (dontShowAgain && currentUser && popupData) {
        const durationDays = popupData.dismissalDuration || 30;
        const validDuration = Math.max(1, durationDays);
        const expirationTime = Date.now() + validDuration * 24 * 60 * 60 * 1000;

        try {
          await setDoc(
            doc(db, 'userDismissals', currentUser.uid),
            {
              expiresAt: Timestamp.fromMillis(expirationTime),
              dismissedAt: Timestamp.now()
            },
            { merge: true }
          );
          localStorage.setItem(`popupDismissed_${currentUser.uid}`, 'true');
        } catch (error) {
          logger.error('Error saving dismissal:', error);
          toast.error(
            `Could not save your choice${error?.message ? `: ${error.message}` : ''}. Please try again.`
          );
          return;
        }
      }
      setIsDismissed(true);
      setShowPopup(false);
    } finally {
      setIsClosing(false);
    }
  };

  if (!showPopup || !popupData || !isHomeOnlyPath) {
    return null;
  }

  // Avoid default 'lg' (800px) — it leaves huge empty bands next to portrait images.
  const modalSize = ['sm', 'lg', 'xl'].includes(popupData.size) ? popupData.size : undefined;
  const dialogLayoutClass =
    popupData.size === 'xl'
      ? ' admin-site-popup-modal--dialog-xl'
      : popupData.size === 'lg'
        ? ' admin-site-popup-modal--dialog-lg'
        : '';
  const showBackdrop =
    popupData.backdrop !== false &&
    popupData.backdrop !== 'false' &&
    popupData.backdrop !== 0;

  const titleText = typeof popupData.title === 'string' ? popupData.title.trim() : '';

  return (
    <Modal
      show={showPopup}
      onHide={handleClose}
      centered
      backdrop={showBackdrop}
      size={modalSize}
      className={`admin-site-popup-modal${dialogLayoutClass}`}
      contentClassName="border-0"
    >
      <Modal.Header closeButton className={`admin-site-popup-header${titleText ? '' : ' admin-site-popup-header--notitle'}`}>
        <Modal.Title as="h2" className="admin-site-popup-title mb-0">
          {titleText || <span className="visually-hidden">Announcement</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="admin-site-popup-body">
        {popupData.imageUrl && (
          <div className="admin-site-popup-image-wrap">
            <img
              src={popupData.imageUrl}
              alt={popupData.title ? `${popupData.title} illustration` : 'Announcement'}
              className="admin-site-popup-image"
              referrerPolicy="no-referrer"
            />
          </div>
        )}
        {popupData.content && (
          <div
            className="admin-site-popup-html"
            dangerouslySetInnerHTML={{ __html: popupData.content }}
          />
        )}
      </Modal.Body>
      <Modal.Footer className="admin-site-popup-footer">
        {popupData.showDontShowAgain ? (
          <Form.Check
            type="checkbox"
            id="admin-popup-dont-show"
            label={`Don't show again for ${popupData.dismissalDuration || 30} days`}
            checked={dontShowAgain}
            onChange={e => setDontShowAgain(e.target.checked)}
          />
        ) : (
          <span />
        )}
        <Button variant="outline-primary" className="admin-site-popup-close" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdminPopup;
